import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const getInitialListState = (location) => {
    return createInitialListState({
        location,
    })
}

const buildListSearch = ({ curPage }) => {
    return buildListSearchQuery({
        curPage,
    })
}

// 운영자가 노출 대상을 빠르게 식별할 수 있도록 기기 코드를 라벨로 변환한다.
const getDeviceTypeLabel = (deviceType) => {
    if (deviceType === 'all') return '전체'
    if (deviceType === 'and') return 'Android'
    if (deviceType === 'ios') return 'iOS'
    return '-'
}

// 팝업 타입 숫자값을 목록에서 바로 읽히는 운영 라벨로 변환한다.
const getPopupTypeLabel = (type) => {
    const popupTypeMap = {
        1: '애니멀런 리스트',
        2: '애니멀런 상세',
        4: '외부 URL 이동',
    }

    return popupTypeMap[Number(type)] || '미지원 타입'
}

// 목록에서는 O/X보다 실제 운영 상태 문구가 더 명확하다.
const getPopupStatusLabel = (status) => Number(status) === 1 ? '노출' : '미노출'

const APP_POPUP_TABLE_COLUMNS = [
    { width: 70 },
    { width: 120 },
    { width: 120 },
    { width: 180 },
    { width: 140 },
    { width: 160 },
    { width: 120 },
]

const APP_POPUP_TABLE_MIN_WIDTH = APP_POPUP_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_POPUP_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

export default function AppPopups({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appPopup/getList', { page: curPage, offset: offset, adminId: adminId, role: role })
            // console.log('data: ',data.data)
            if (data.code === 200) {
                setTotalCount(data.data?.count || 0)
                setParams(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 랜덤 팝업 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 랜덤 팝업 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appPopup/delete', { popupId: id })
                // console.log('deleteData: ',data)
                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)
                    if (curPage > 1 && params.length === 1) {
                        setCurPage(curPage - 1)
                    } else {
                        await getData(false)
                    }
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 랜덤 팝업을 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    // 필터가 없는 목록이므로 오류와 기본 빈 목록만 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 랜덤 팝업 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 App 랜덤 팝업이 없습니다."
                description="팝업을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 랜덤 팝업 리스트</Title>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appPopup',
                                    search: location.search,
                                    curPage: curPage,
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_POPUP_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_POPUP_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-popup-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>노출기종</TableCell>
                                <TableCell>노출여부</TableCell>
                                <TableCell>구분</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? params.map((row) => (
                                <TableRow key={row.popupId}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell sx={APP_POPUP_LABEL_CELL_SX}>{getDeviceTypeLabel(row.deviceType)}</TableCell>
                                    <TableCell sx={APP_POPUP_LABEL_CELL_SX}>{getPopupStatusLabel(row.status)}</TableCell>
                                    <TableCell sx={APP_POPUP_LABEL_CELL_SX}>{getPopupTypeLabel(row.type)}</TableCell>
                                    <TableCell>
                                        {row.image ? (
                                            <Box
                                                component="img"
                                                src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                                                alt="팝업 이미지"
                                                sx={{
                                                    width: 72,
                                                    height: 40,
                                                    objectFit: 'cover',
                                                    border: '1px solid rgba(148, 163, 184, 0.25)',
                                                    borderRadius: '4px',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.landingUrl ? (
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => {
                                                    window.open(row.landingUrl, '_blank', 'noopener noreferrer')
                                                }}
                                            >
                                                열기
                                            </Button>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup disabled={actionLoading}>
                                            <EditIcon path={'appPopup'} id={row.popupId} search={location.search} curPage={curPage}/>
                                            <DeleteAlertDialog handleDelete={handleDelete} id={row.popupId}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
